import type { ReactNode } from 'react';
import type { Finding, Severity, Status, Category } from '../engine/types';

export function Card({
  title,
  subtitle,
  actions,
  children,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-5 rounded-xl border border-orange-500/12 bg-zinc-950/80 py-5 shadow-[0_24px_80px_rgba(0,0,0,0.42)]">
      {/* Render the header when ANY of title/subtitle/actions is present —
          otherwise a subtitle-only card silently renders nothing. */}
      {(title || subtitle || actions) && (
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-white/5 px-5 pb-4">
          <div className="min-w-0">
            {title && <h2 className="text-lg font-semibold text-zinc-100">{title}</h2>}
            {subtitle && <p className="mt-1 text-sm text-zinc-500">{subtitle}</p>}
          </div>
          {actions}
        </header>
      )}
      <div className="px-5 pb-1">{children}</div>
    </section>
  );
}

const STATUS_STYLES: Record<Status, string> = {
  ok: 'border-emerald-500/25 bg-emerald-500/10 text-emerald-300',
  unavailable: 'border-zinc-700 bg-zinc-800/60 text-zinc-400',
  denied: 'border-amber-500/25 bg-amber-500/10 text-amber-300',
  suspended: 'border-sky-500/25 bg-sky-500/10 text-sky-300',
  unsupported: 'border-zinc-700 bg-zinc-800/60 text-zinc-400',
  error: 'border-rose-500/25 bg-rose-500/10 text-rose-300',
  timeout: 'border-amber-500/25 bg-amber-500/10 text-amber-300',
  skipped: 'border-zinc-700 bg-zinc-800/60 text-zinc-500',
};

const SEVERITY_STYLES: Record<Severity, string> = {
  info: 'border-zinc-700 bg-zinc-800/60 text-zinc-300',
  low: 'border-sky-500/25 bg-sky-500/10 text-sky-300',
  medium: 'border-amber-500/30 bg-amber-500/10 text-amber-200',
  high: 'border-rose-500/30 bg-rose-500/10 text-rose-200',
};

const CATEGORY_LABELS: Record<Category, string> = {
  hardware: 'Hardware / OS',
  browser: 'Browser',
  network: 'Network / proxy',
  behaviour: 'Behaviour',
};

export type BadgeTone = Status | 'accent' | 'neutral';

/**
 * Status is conveyed by text + border, not colour alone, so it is legible
 * without colour perception and survives a monochrome screenshot.
 */
export function Badge({
  children,
  tone = 'neutral',
  title,
}: {
  children: ReactNode;
  tone?: BadgeTone;
  title?: string;
}) {
  const cls =
    tone === 'accent'
      ? 'border-orange-500/25 bg-orange-500/10 text-orange-200'
      : tone === 'neutral'
        ? 'border-zinc-700 bg-zinc-800/60 text-zinc-300'
        : STATUS_STYLES[tone] ?? STATUS_STYLES.unavailable;
  return (
    <span
      title={title}
      className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${cls}`}
    >
      {children}
    </span>
  );
}

export function SeverityBadge({ severity }: { severity: Severity }) {
  return (
    <span
      className={`inline-flex items-center rounded border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${SEVERITY_STYLES[severity]}`}
    >
      {severity}
    </span>
  );
}

export function CategoryLabel({ category }: { category: Category }) {
  return <span className="text-zinc-500">{CATEGORY_LABELS[category]}</span>;
}

/** Renders an arbitrary signal value as readable monospace text. */
export function SignalValueText({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return <span className="text-zinc-600">—</span>;
  }
  if (typeof value === 'boolean') {
    return <span className={value ? 'text-emerald-300' : 'text-zinc-400'}>{String(value)}</span>;
  }
  if (typeof value === 'number') return <span className="text-orange-200">{String(value)}</span>;
  if (typeof value === 'string') {
    return <span className="break-all text-zinc-200">{value}</span>;
  }
  return (
    <pre className="max-h-64 overflow-auto rounded-lg border border-white/5 bg-black/40 p-2.5 text-xs leading-5 text-zinc-300">
      {safeJson(value)}
    </pre>
  );
}

function safeJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    return String(value);
  }
}

export function FindingRow({ finding }: { finding: Finding }) {
  return (
    <details className="rounded-xl border border-white/6 bg-black/20 px-4 py-3">
      <summary className="flex cursor-pointer list-none flex-wrap items-center gap-2.5">
        <SeverityBadge severity={finding.severity} />
        <span className="text-sm font-medium text-zinc-100">{finding.title}</span>
        <span className="ml-auto text-xs text-zinc-500">
          <CategoryLabel category={finding.category} /> · confidence {finding.confidence}
        </span>
      </summary>
      <div className="mt-3 space-y-3 text-sm leading-6 text-zinc-400">
        <p>{finding.detail}</p>
        {finding.evidence.length > 0 && (
          <div>
            <div className="mb-1 text-xs uppercase tracking-wider text-zinc-600">Evidence</div>
            <ul className="space-y-0.5 font-mono text-xs text-zinc-300">
              {finding.evidence.map((e, i) => (
                <li key={i} className="break-all">
                  {e}
                </li>
              ))}
            </ul>
          </div>
        )}
        {finding.sources.length > 0 && (
          <div>
            <div className="mb-1 text-xs uppercase tracking-wider text-zinc-600">Sources</div>
            <ul className="space-y-0.5 text-xs">
              {finding.sources.map((s) => (
                <li key={s}>
                  <a
                    href={s}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-orange-300 underline underline-offset-2 hover:text-orange-200"
                  >
                    {s}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </details>
  );
}

export function SectionTitle({ children, id }: { children: ReactNode; id?: string }) {
  return (
    <h2 id={id} className="text-xl font-semibold tracking-tight text-white">
      {children}
    </h2>
  );
}

export function Prose({ children }: { children: ReactNode }) {
  return <div className="space-y-3 text-sm leading-6 text-zinc-400">{children}</div>;
}
